// All keys (parent + legacy + tab-level) related to a consolidated section.
// Used for sidebar visibility and route-guard fallback.
export const PAGE_KEY_MAPPINGS: Record<string, string[]> = {
  CUSTOMS_ERP: [
    "CUSTOMS_ERP", "TRADE_DATA", "DOCUMENTS", "PRODUCT_HUB",
    "CUSTOMS_ERP_PRODUCTS", "CUSTOMS_ERP_SUPPLIERS", "CUSTOMS_ERP_ENTRIES", "CUSTOMS_ERP_DOCUMENTS",
  ],
  COMPLIANCE: [
    "COMPLIANCE", "POST_IMPORT_ADJUSTMENT",
    "COMPLIANCE_VERIFICATION", "COMPLIANCE_TARIFF_ANALYSIS", "COMPLIANCE_POST_IMPORT", "COMPLIANCE_CERTIFICATE_OF_ORIGIN",
  ],
  TOOLS: [
    "TOOLS", "SCENARIO_MODELING",
    "TOOLS_DUTY_CALCULATOR", "TOOLS_UNIT_COST_ANALYSIS", "TOOLS_COMMERCE_DATA", "TOOLS_SCENARIO_MODELING", "TOOLS_IEEPA_REFUND_CAPE",
  ],
  POTENTIAL_SAVINGS: [
    "POTENTIAL_SAVINGS",
    "POTENTIAL_SAVINGS_PREFERENTIAL_ORIGIN", "POTENTIAL_SAVINGS_FTZ", "POTENTIAL_SAVINGS_301_EXCLUSIONS",
  ],
};

// Parent/legacy keys that grant FULL access to all tabs in a section.
// Tab-level keys are excluded — they only grant their specific tab.
export const FULL_ACCESS_KEYS: Record<string, string[]> = {
  CUSTOMS_ERP: ["CUSTOMS_ERP", "TRADE_DATA", "DOCUMENTS", "PRODUCT_HUB"],
  COMPLIANCE: ["COMPLIANCE", "POST_IMPORT_ADJUSTMENT"],
  TOOLS: ["TOOLS", "SCENARIO_MODELING"],
  POTENTIAL_SAVINGS: ["POTENTIAL_SAVINGS"],
};

// Bidirectional equivalence between legacy and new consolidated keys.
// Mirrors the server-side PAGE_KEY_EQUIVALENCE so sidebar/RequirePage grant
// access whether the user holds the legacy or new side of a pair.
export const PAGE_KEY_EQUIVALENCE: Record<string, string[]> = {
  TRADE_DATA: ["CUSTOMS_ERP_ENTRIES"],
  CUSTOMS_ERP_ENTRIES: ["TRADE_DATA"],
  DOCUMENTS: ["CUSTOMS_ERP_DOCUMENTS"],
  CUSTOMS_ERP_DOCUMENTS: ["DOCUMENTS"],
  PRODUCT_HUB: ["CUSTOMS_ERP_PRODUCTS"],
  CUSTOMS_ERP_PRODUCTS: ["PRODUCT_HUB"],
  POST_IMPORT_ADJUSTMENT: ["COMPLIANCE_POST_IMPORT"],
  COMPLIANCE_POST_IMPORT: ["POST_IMPORT_ADJUSTMENT"],
  SCENARIO_MODELING: ["TOOLS_SCENARIO_MODELING"],
  TOOLS_SCENARIO_MODELING: ["SCENARIO_MODELING"],
};

// Sub-key -> parent key that implies it
export const TAB_KEY_PARENTS: Record<string, string> = {
  CUSTOMS_ERP_PRODUCTS: "CUSTOMS_ERP",
  CUSTOMS_ERP_SUPPLIERS: "CUSTOMS_ERP",
  CUSTOMS_ERP_ENTRIES: "CUSTOMS_ERP",
  CUSTOMS_ERP_DOCUMENTS: "CUSTOMS_ERP",
  COMPLIANCE_VERIFICATION: "COMPLIANCE",
  COMPLIANCE_TARIFF_ANALYSIS: "COMPLIANCE",
  COMPLIANCE_POST_IMPORT: "COMPLIANCE",
  COMPLIANCE_CERTIFICATE_OF_ORIGIN: "COMPLIANCE",
  TOOLS_DUTY_CALCULATOR: "TOOLS",
  TOOLS_UNIT_COST_ANALYSIS: "TOOLS",
  TOOLS_COMMERCE_DATA: "TOOLS",
  TOOLS_SCENARIO_MODELING: "TOOLS",
  TOOLS_IEEPA_REFUND_CAPE: "TOOLS",
  POTENTIAL_SAVINGS_PREFERENTIAL_ORIGIN: "POTENTIAL_SAVINGS",
  POTENTIAL_SAVINGS_FTZ: "POTENTIAL_SAVINGS",
  POTENTIAL_SAVINGS_301_EXCLUSIONS: "POTENTIAL_SAVINGS",
  FIRST_SALE: "POTENTIAL_SAVINGS",
};

// Check if user has access to a page or tab, considering parent keys,
// legacy mappings, and legacy↔new equivalence.
export const hasPageAccess = (pages: string[], pageKey: string): boolean => {
  // Direct match
  if (pages.includes(pageKey)) return true;

  // Parent key implies all tab-level children
  const parent = TAB_KEY_PARENTS[pageKey];
  if (parent && pages.includes(parent)) return true;

  // Legacy ↔ new equivalence (e.g. TRADE_DATA ⇔ CUSTOMS_ERP_ENTRIES)
  const equiv = PAGE_KEY_EQUIVALENCE[pageKey];
  if (equiv && equiv.some((key) => pages.includes(key))) return true;

  // Check all related keys for consolidated page keys
  const relatedKeys = PAGE_KEY_MAPPINGS[pageKey];
  if (relatedKeys) {
    return relatedKeys.some((key) => pages.includes(key));
  }

  return false;
};

// Check if user has FULL access to a section (parent or legacy key, not just a tab key).
export const hasFullSectionAccess = (pages: string[], section: string): boolean => {
  const keys = FULL_ACCESS_KEYS[section];
  if (!keys) return pages.includes(section);
  return keys.some((k) => pages.includes(k));
};
